import { Trans, useTranslation } from 'react-i18next';
import { Field, Form, Formik, useField } from 'formik';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import MuiTextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SaveIcon from '@mui/icons-material/Save';

import { Button, ExpandIcon, Page, TextField } from 'components';
import LeoCheckBox from 'components/input-fields/leoCheckBox/LeoCheckBox';
import { Env } from 'config/env';
import {
  FieldDevicePhotoResolutionModel,
  FieldDeviceSettingsModel,
  PublicUtilityType,
} from 'models';
import {
  toResolution,
  toResolutionOption,
  useFieldDeviceSettings,
  usePhotoResolutionOptions,
  useUpdateFieldDeviceSettings,
} from 'features/fieldDeviceSettings';
import { useCanEdit } from 'utils/auth';

import { SETTING_SECTIONS, SettingRowConfig, UTILITY_COLUMNS } from './fieldDeviceSettingsConfig';
import { validationSchema } from './fieldDeviceSettingsValidation';

const VALUE_COLUMN_WIDTH = 220;

const styles = {
  page: {
    height: '100vh',
    overflow: 'hidden',
    pb: 3,
    '& > .MuiBox-root': {
      minHeight: 0,
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  header: {
    display: 'flex',
    mb: 1,
  },
  divider: {
    mb: 2,
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  utilityHeaderCell: {
    width: VALUE_COLUMN_WIDTH,
    textAlign: 'center',
  },
  range: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    maxWidth: VALUE_COLUMN_WIDTH,
  },
  valueCell: {
    '& .MuiFormGroup-root': {
      alignItems: 'center',
    },
    '& .MuiFormControlLabel-root': {
      m: 0,
    },
  },
};

interface SettingInputProps {
  row: SettingRowConfig;
  utilityType?: PublicUtilityType;
  disabled: boolean;
}

const ResolutionInput = ({ name, disabled }: { name: string; disabled: boolean }) => {
  const [{ value }, , { setValue }] = useField<FieldDevicePhotoResolutionModel | undefined>(name);
  const { data: resolutionOptions = [] } = usePhotoResolutionOptions();
  const selected = value ? toResolutionOption(value) : '';
  // The backend accepts any size, so a stored value that is not in the list is still offered.
  const options =
    selected && !resolutionOptions.includes(selected)
      ? [selected, ...resolutionOptions]
      : resolutionOptions;

  return (
    <MuiTextField
      select
      fullWidth
      variant={Env.DEFAULT_INPUT_VARIANT}
      margin="none"
      value={selected}
      disabled={disabled}
      onChange={(event) => setValue(toResolution(event.target.value))}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </MuiTextField>
  );
};

const NumberInput = ({
  name,
  maxLength,
  disabled,
  ariaLabel,
}: {
  name: string;
  maxLength?: number;
  disabled: boolean;
  ariaLabel?: string;
}) => (
  <Field
    name={name}
    component={TextField}
    disabled={disabled}
    margin="none"
    digitsOnly
    inputProps={{ maxLength, inputMode: 'numeric', 'aria-label': ariaLabel }}
  />
);

const RangeInput = ({
  row,
  name,
  disabled,
}: {
  row: SettingRowConfig;
  name: string;
  disabled: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={styles.range}>
      <NumberInput
        name={`${name}.min`}
        maxLength={row.maxLength}
        disabled={disabled}
        ariaLabel={t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_COUNT_MIN')}
      />
      <Typography>–</Typography>
      <NumberInput
        name={`${name}.max`}
        maxLength={row.maxLength}
        disabled={disabled}
        ariaLabel={t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_COUNT_MAX')}
      />
    </Box>
  );
};

const SettingInput = ({ row, utilityType, disabled }: SettingInputProps) => {
  const name = utilityType ? `utilityTypes.${utilityType}.${row.name}` : row.name;

  if (row.type === 'checkbox') {
    return <LeoCheckBox name={name} disabled={disabled} />;
  }

  if (row.type === 'resolution') {
    return <ResolutionInput name={name} disabled={disabled} />;
  }

  if (row.type === 'range') {
    return <RangeInput row={row} name={name} disabled={disabled} />;
  }

  return <NumberInput name={name} maxLength={row.maxLength} disabled={disabled} />;
};

const FieldDeviceSettingsPage = () => {
  const { t } = useTranslation();
  const canEdit = useCanEdit('FIELD_DEVICE_SETTINGS');

  const { data: settings, isLoading } = useFieldDeviceSettings();
  const { mutateAsync: updateSettings, isPending } = useUpdateFieldDeviceSettings();

  const handleSubmit = async (values: FieldDeviceSettingsModel) => {
    await updateSettings(validationSchema.cast(values) as FieldDeviceSettingsModel);
  };

  return (
    <Page
      title={<Trans i18nKey="KOZOS_TEREPI_ESZKOZ_BEALLITASOK.TITLE" />}
      sx={styles.page}
      isLoading={isLoading}
    >
      {settings && (
        <Formik
          initialValues={settings}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ dirty, isValid }) => (
            <Box component={Form} sx={styles.form}>
              <Box sx={styles.header}>
                <Button
                  type="submit"
                  size="small"
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  disabled={!canEdit || !dirty || !isValid}
                  loading={isPending}
                >
                  <Trans i18nKey="COMMON.SAVE" />
                </Button>
              </Box>
              <Divider sx={styles.divider} />

              <Box sx={styles.scrollArea}>
                {SETTING_SECTIONS.map((section) => (
                  <Accordion key={section.key} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandIcon open />}>
                      <Typography variant="subtitle1">{t(section.title)}</Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Trans i18nKey="KOZOS_TEREPI_ESZKOZ_BEALLITASOK.COLUMN_SETTING" />
                            </TableCell>

                            {UTILITY_COLUMNS.map((column) => (
                              <TableCell key={column.utilityType} sx={styles.utilityHeaderCell}>
                                {t(column.label)}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.rows.map((row) => (
                            <TableRow key={row.name} sx={{ verticalAlign: 'top' }}>
                              <TableCell>
                                <Typography variant="body2">{t(row.label)}</Typography>
                                {row.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {t(row.description)}
                                  </Typography>
                                )}
                              </TableCell>
                              {row.isGlobal ? (
                                <TableCell colSpan={UTILITY_COLUMNS.length} sx={styles.valueCell}>
                                  <SettingInput row={row} disabled={!canEdit} />
                                </TableCell>
                              ) : (
                                UTILITY_COLUMNS.map((column) => (
                                  <TableCell key={column.utilityType} sx={styles.valueCell}>
                                    {!row.hiddenFor?.includes(column.utilityType) && (
                                      <SettingInput
                                        row={row}
                                        utilityType={column.utilityType}
                                        disabled={!canEdit}
                                      />
                                    )}
                                  </TableCell>
                                ))
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          )}
        </Formik>
      )}
    </Page>
  );
};

export default FieldDeviceSettingsPage;
